import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomChartMetricPayload, IndicatorSelectHandler } from '@/entities/dashboards';
import { preventShiftSelection, richTextToPlain } from '@/shared/lib';
import { RichTextContent } from '@/shared/ui';
import { KpiCard } from '../../parts/KpiCard';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS, chartNumberDigits, fmt } from '../../utils';

/** Длина значения, с которой оно перестаёт помещаться крупным начертанием карточки. */
const COMPACT_VALUE_LENGTH = 12;

type Props = {
  title: string;
  payload: CustomChartMetricPayload;
  /** Переход к диалогам, из которых посчитан показатель; не задан — показатель нажатий не принимает */
  onSelect?: IndicatorSelectHandler;
};

/**
 * Значение показателя: текст результата собирает бэкенд, поэтому показатель выражает и число,
 * и найденную позицию. Числовое оформление (единица измерения, потолок шкалы) добавляется,
 * только когда своего текста нет: у доли единица «%» уже говорит о потолке в 100.
 *
 * Набран текст результата с оформлением, поэтому значение показателя — разметка;
 * собранное из числа значение — её частный случай.
 */
const metricValue = (payload: CustomChartMetricPayload): string => {
  if (payload.text) return payload.text;
  // Ряд показателя — его единственная величина: счёт показан целым, среднее — с дробной частью.
  const value = fmt(payload.value, chartNumberDigits([payload.value]));
  if (payload.unit) return `${value}${payload.unit}`;
  if (payload.maxValue !== undefined)
    return `${value} / ${fmt(payload.maxValue, chartNumberDigits([payload.maxValue]))}`;
  return value;
};

/**
 * Показатель пользовательского дашборда.
 *
 * Показатель, за которым стоят разговоры, открывает их список — как показатели стандартного
 * дашборда. Название найденной позиции бывает длинным, поэтому значение показывается уменьшенным.
 *
 * С погашенным переходом показатель остаётся тем же блоком, но нажимаемым не показан: так он
 * выглядит в режиме правки состава дашборда.
 */
export const MetricChartView: FC<Props> = ({ title, payload, onSelect }) => {
  const { t } = useTranslation('qualityDashboard');

  const value = metricValue(payload);
  const conversationIds = payload.conversationIds ?? [];
  // Название найденной позиции длиннее числа — такое значение показываем меньшим начертанием.
  const card = (
    <KpiCard
      label={title}
      value={<RichTextContent html={value} />}
      compact={richTextToPlain(value).length > COMPACT_VALUE_LENGTH}
    />
  );

  if (!onSelect || conversationIds.length === 0) return card;

  return (
    <div
      role="button"
      tabIndex={-1}
      aria-label={t('presets.chart.openDialogs')}
      onMouseDown={preventShiftSelection}
      onClick={(event) =>
        onSelect({ label: title, conversationIds, sourceColumns: payload.sourceColumns }, event.shiftKey)
      }
      className={DASHBOARD_CLICKABLE_BLOCK_CLASS}
    >
      {card}
    </div>
  );
};
